"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { createClient } from "@/libs/supabase/client";
import { User } from "@supabase/supabase-js";
import { useRouter } from "next/navigation";
import { Marquee } from "@/components/magicui/marquee";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";

interface HeroProps {
  isMobile: boolean;
}

export default function Hero({ isMobile }: HeroProps) {
  const [mounted, setMounted] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [supabase, setSupabase] = useState<any>(null);
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
    
    // Only create the Supabase client in the browser
    if (typeof window !== 'undefined') {
      setSupabase(createClientComponentClient());
    }
  }, []);

  useEffect(() => {
    if (!supabase) return;

    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: any, session: any) => {
      setUser(session?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  const profiles = [
    "/1_hero/profiles/Frame 33.png",
    "/1_hero/profiles/Frame 34.png",
    "/1_hero/profiles/Frame 35.png",
    "/1_hero/profiles/Frame 36.png",
    "/1_hero/profiles/Frame 37.png",
    "/1_hero/profiles/Frame 38.png",
    "/1_hero/profiles/Frame 39.png",
    "/1_hero/profiles/Frame 40.png",
    "/1_hero/profiles/Frame 41.png",
    "/1_hero/profiles/Frame 42.png",
    "/1_hero/profiles/Frame 43.png",
    "/1_hero/profiles/Frame 44.png",
  ];

  const handleCommunityClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    router.push('/auth/signin');
  };

  return (
    <section className="flex flex-col items-center justify-center pt-28 sm:pt-44 pb-16 sm:pb-20 px-4 bg-black">
      <div className="max-w-7xl mx-auto text-center">
        <div className="flex justify-center mb-6">
          <span className={`inline-flex items-center gap-2 border border-white/15 bg-white/5 text-white/80 ${isMobile ? 'rounded-2xl px-3 py-2 text-xs leading-relaxed text-left' : 'rounded-full px-4 py-2 text-sm'}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
            Built by Jason Zhou · ex-Head of Product @ Relevance AI · 8M+ YouTube views
          </span>
        </div>

        <h1 className={`${isMobile ? 'text-5xl' : 'text-6xl'} font-bold mb-5 tracking-tight`}>
          Become Top 1%
          <br className="md:hidden" /> AI Builder
        </h1>

        <p className={`${isMobile ? 'text-xl' : 'text-2xl'} text-white/70 max-w-xl mx-auto mb-6`}>
          Learn AI coding, Agents, LLM applications
        </p>

        {/* Profile Images */}
        <div className="flex justify-center mb-6 overflow-x-auto">
          <div className={`flex ${isMobile ? 'flex-wrap justify-center gap-2' : ''}`}>
            {profiles.slice(0, isMobile ? 6 : profiles.length).map((profile, i) => (
              <div
                key={i}
                className={`relative w-12 h-12 rounded-full overflow-hidden border-2 border-black ${
                  isMobile ? '' : '-ml-2 first:ml-0'
                }`}
                style={{ zIndex: 20 - i }}
              >
                <Image
                  src={profile}
                  alt={`Community member ${i + 1}`}
                  fill
                  className="object-cover"
                />
              </div>
            ))}
            <div
              className={`relative ${isMobile ? '' : '-ml-2'} w-20 h-12 rounded-full bg-black border-2 border-black flex items-center justify-center text-sm text-white/70`}
              style={{ zIndex: 1 }}
            >
              +1,281
            </div>
          </div>
        </div>

        <div className="mb-20">
          <a
            href="https://community.aibuilderclub.com"
            onClick={handleCommunityClick}
            className={`inline-flex items-center bg-white text-black ${
              isMobile ? 'px-6 py-2.5 text-sm' : 'px-8 py-3 text-md'
            } rounded-full font-medium hover:bg-white/90 transition-colors group`}
          >
            <span className="font-sans">
              {user ? 'Enter Community' : 'Join the Community'}
            </span>
            <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
          </a>
        </div>

        {/* Company Logos */}
        <div className="text-center">
          <p className={`text-white/50 ${isMobile ? 'text-xs' : 'text-sm'} mb-10`}>
            Members include engineers, designers & products from
          </p>

          <div className="relative w-full overflow-hidden">
            {isMobile ? (
              <div className="flex flex-wrap justify-center gap-8 px-4">
                <Image
                  src="/1_hero/logos/helicone.png"
                  alt="Helicone"
                  width={100}
                  height={28}
                  style={{ height: "28px", width: "auto" }}
                />
                <Image
                  src="/1_hero/logos/llamaindex.png"
                  alt="LlamaIndex"
                  width={100}
                  height={28}
                  style={{ height: "28px", width: "auto" }}
                />
                <Image
                  src="/1_hero/logos/windsurf.png"
                  alt="Windsurf"
                  width={100}
                  height={28}
                  style={{ height: "28px", width: "auto" }}
                />
                <Image
                  src="/1_hero/logos/replicate.png"
                  alt="Replicate"
                  width={100}
                  height={28}
                  style={{ height: "28px", width: "auto" }}
                />
                <Image
                  src="/1_hero/logos/microsoft.png"
                  alt="Microsoft"
                  width={100}
                  height={28}
                  style={{ height: "28px", width: "auto" }}
                />
                <Image
                  src="/1_hero/logos/bytedance.png"
                  alt="ByteDance"
                  width={100}
                  height={28}
                  style={{ height: "28px", width: "auto" }}
                />
                <Image
                  src="/1_hero/logos/firecrawl.png"
                  alt="Firecrawl"
                  width={100}
                  height={28}
                  style={{ height: "28px", width: "auto" }}
                />
                <Image
                  src="/1_hero/logos/google.png"
                  alt="Google"
                  width={100}
                  height={28}
                  style={{ height: "28px", width: "auto" }}
                />
              </div>
            ) : (
              <>
                <Marquee className="[--duration:12s]">
                  <div className="flex items-center gap-12">
                    <Image
                      src="/1_hero/logos/helicone.png"
                      alt="Helicone"
                      width={100}
                      height={28}
                      style={{ height: "28px", width: "auto" }}
                    />
                    <Image
                      src="/1_hero/logos/llamaindex.png"
                      alt="LlamaIndex"
                      width={100}
                      height={28}
                      style={{ height: "28px", width: "auto" }}
                    />
                    <Image
                      src="/1_hero/logos/windsurf.png"
                      alt="Windsurf"
                      width={100}
                      height={28}
                      style={{ height: "28px", width: "auto" }}
                    />
                    <Image
                      src="/1_hero/logos/replicate.png"
                      alt="Replicate"
                      width={100}
                      height={28}
                      style={{ height: "28px", width: "auto" }}
                    />
                    <Image
                      src="/1_hero/logos/microsoft.png"
                      alt="Microsoft"
                      width={100}
                      height={28}
                      style={{ height: "28px", width: "auto" }}
                    />
                    <Image
                      src="/1_hero/logos/bytedance.png"
                      alt="ByteDance"
                      width={100}
                      height={28}
                      style={{ height: "28px", width: "auto" }}
                    />
                    <Image
                      src="/1_hero/logos/firecrawl.png"
                      alt="Firecrawl"
                      width={100}
                      height={28}
                      style={{ height: "28px", width: "auto" }}
                    />
                    <Image
                      src="/1_hero/logos/google.png"
                      alt="Google"
                      width={100}
                      height={28}
                      style={{ height: "28px", width: "auto" }}
                    />
                  </div>
                </Marquee>
                <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-black"></div>
                <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-black"></div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
